/** Docs (UA): see modules/game/DOCS.md — full-screen game overlay */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GAME_CONFIG as C, GAME_PALETTE as P, GameTheme } from '../../game.constants';
import { createGameEngine, GameEngine, GameEvent } from '../../lib/engine';
import { useFieldLayout } from '../../hooks/useFieldLayout';
import { FieldLayout, FIELD } from '../../lib/layout';
import { createFx, drawFrame, fxCatchBurst, fxHitBurst, FxState, renderBackground } from '../../lib/render';
import { createGameSound, GameSound } from '../../lib/sound';
import { createSprites, SpriteSet } from '../../lib/sprites';
import { GiftBoxIcon, HeartIcon, NoteIcon } from '../icons';
import styles from './GameScreen.module.css';

type Phase = 'start' | 'play' | 'end';

/** Which button ended the overlay: 'done' is the button back to the success
 *  screen after a finished game, 'dismissed' is «not now» on the start screen.
 *  Only the first one is a return-to-success in the funnel (SWRN-1898). */
export type GameCloseReason = 'done' | 'dismissed';

interface GameEndSummary {
    score: number;
    won: boolean;
    burned: boolean;
    seconds: number;
    isNewRecord: boolean;
}

export interface GameScreenProps {
    theme: GameTheme;
    record: number;
    soundEnabled: boolean;
    onSoundToggle: (on: boolean) => void;
    /** Called once when the game actually starts — the try burns here. */
    onTryStart: () => void;
    /** Called the moment the threshold is crossed — accrue the point NOW,
     *  losing remaining lives never takes it back. */
    onThreshold: () => void;
    onFinish: (score: number) => boolean;
    /** Fires once per finished game — the container logs the outcome; the
     *  overlay itself knows nothing about telemetry. Only won/lost travels:
     *  the score is deliberately not logged. */
    onGameEnd: (won: boolean) => void;
    onClose: (reason: GameCloseReason) => void;
}

const FONTS_ID = 'game-catch-fonts';

/** Unbounded loads lazily with the game only, and only for the headline and
 *  the final score; everything else is Commissioner, already in the app. */
const injectFonts = (): void => {
    if (document.getElementById(FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = FONTS_ID;
    link.rel = 'stylesheet';
    link.href =
        'https://fonts.googleapis.com/css2?family=Unbounded:wght@700;900&display=swap';
    document.head.appendChild(link);
};

/** Sprite canvases carry transparent padding (the golden item's frame is half
 *  halo, the bug's legs reach the very edge), so a preview draws the sprite 1:1
 *  and lets CSS size it — zooming inside a fixed 44px box cropped the artwork.
 *  Sizes are the mockup ones (Figma «☕ Game v4 — Espresso Brand», start screen). */
const PREVIEW: Record<'item' | 'golden' | 'bug' | 'bugLight', { frame: number; css: number }> = {
    item: { frame: 48, css: 38 },
    golden: { frame: 64, css: 51 },
    bug: { frame: 48, css: 44 },
    bugLight: { frame: 48, css: 44 },
};

/** Palette → CSS custom properties: the stylesheet reads var(--game-*), so the
 *  palette in game.constants.ts stays the only place a colour is written. */
const GAME_CSS_VARS = Object.fromEntries(
    Object.entries(P).flatMap(([token, hex]) => {
        const name = `--game-${token.replace(/[A-Z0-9]/g, (ch) => `-${ch.toLowerCase()}`)}`;
        const n = parseInt(hex.slice(1), 16);
        // rgba() in the stylesheet needs the channels, not the hex.
        return [
            [name, hex],
            [`${name}-rgb`, `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`],
        ];
    }),
) as React.CSSProperties;

const CONFETTI_COLORS = [P.gold, P.accentAlt, P.cream, P.success];

const SpritePreview = ({
    sprites,
    kind,
    cssSize,
}: {
    sprites: SpriteSet | null;
    kind: 'item' | 'golden' | 'bug' | 'bugLight';
    cssSize?: number;
}) => {
    const ref = useRef<HTMLCanvasElement>(null);
    const { frame, css } = PREVIEW[kind];
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas || !sprites) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, frame, frame);
        ctx.drawImage(sprites[kind], 0, 0, frame, frame);
    }, [sprites, kind, frame]);
    const size = cssSize ?? css;
    return <canvas ref={ref} width={frame} height={frame} style={{ width: size, height: size }} />;
};

/** Field backdrop (canopy, bokeh, ember, puddle) behind start/end screens —
 *  in the mockups every game screen lives on the full Field, not a flat
 *  gradient. Rendered once via the same cached renderBackground(). */
const BackgroundCanvas = ({ layout }: { layout: FieldLayout }) => {
    const ref = useRef<HTMLCanvasElement>(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const bg = renderBackground(layout);
        canvas.width = bg.width;
        canvas.height = bg.height;
        canvas.getContext('2d')?.drawImage(bg, 0, 0);
    }, [layout]);
    return <canvas ref={ref} className={styles.bgCanvas} aria-hidden />;
};

const CatcherPreview = ({ sprites }: { sprites: SpriteSet | null }) => {
    const ref = useRef<HTMLCanvasElement>(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas || !sprites) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, 96, 64);
        ctx.drawImage(sprites.catcher, 0, 0, 96, 64);
    }, [sprites]);
    return <canvas ref={ref} width={96} height={64} className={styles.catcherDock} />;
};

const GameScreen = ({
    theme,
    record,
    soundEnabled,
    onSoundToggle,
    onTryStart,
    onThreshold,
    onFinish,
    onGameEnd,
    onClose,
}: GameScreenProps) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState<Phase>('start');
    const [multActive, setMultActive] = useState(false);
    const [multEnding, setMultEnding] = useState(false);
    const [livesLeft, setLivesLeft] = useState<number>(C.LIVES);
    const [end, setEnd] = useState<GameEndSummary | null>(null);
    // Sprites in state, not a ref: previews render only after pre-rendering
    // finishes, and a ref would never re-render the start screen.
    const [sprites, setSprites] = useState<SpriteSet | null>(null);

    // HUD в стані, а не в рефі: козирок під ним малюється за ЙОГО виміряною
    // висотою, тож поява елемента має перезапустити спостерігача.
    const [hudEl, setHudEl] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const { layout, layoutRef } = useFieldLayout(overlayRef, hudEl);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const barFillRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<HTMLSpanElement>(null);
    // Обʼєкт спрайтів може змінитись серед партії (перепікання під новий
    // масштаб), тож цикл залежить від ГОТОВНОСТІ, а сам набір читає з рефа.
    const spritesReady = sprites !== null;

    const engineRef = useRef<GameEngine | null>(null);
    const soundRef = useRef<GameSound | null>(null);
    const backgroundRef = useRef<HTMLCanvasElement | null>(null);
    const spritesRef = useRef<SpriteSet | null>(null);
    const spriteScaleRef = useRef(0);
    const pausedRef = useRef(false);
    const multPieRef = useRef<HTMLSpanElement>(null);
    const targetXRef = useRef(0.5);
    const streakRef = useRef(0);

    useEffect(() => {
        injectFonts();
        soundRef.current = createGameSound();
        soundRef.current.setEnabled(soundEnabled);
    }, []);

    /** Спрайти печуться під ФАКТИЧНИЙ масштаб (device px на ref-одиницю), а не
     *  під голий dpr: на планшеті сцена росте до 1.25×, і запечена під 390
     *  текстура там помітно мила б. Перепікаємо лише за виходом за ±25% —
     *  інакше кожен рух адресного рядка Safari перемальовував би весь набір. */
    useEffect(() => {
        const target = Math.min(Math.max(layout.scale, 1), 3);
        const baked = spriteScaleRef.current;
        if (baked > 0 && Math.abs(target - baked) / baked < 0.25) return;
        spriteScaleRef.current = target;
        const next = createSprites(theme, target);
        spritesRef.current = next;
        setSprites(next);
    }, [theme, layout.scale]);

    /** Ландшафт на телефоні: поле стиснуте так, що гра нечесна. Таймер стає,
     *  доки гравець не поверне девайс — випадковий поворот не коштує балу. */
    useEffect(() => {
        pausedRef.current = !layout.playable;
    }, [layout.playable]);

    useEffect(() => {
        soundRef.current?.setEnabled(soundEnabled);
    }, [soundEnabled]);

    /** Гра — повноекранний fixed-оверлей, але сторінка під ним лишається
     *  скрольною: палець тягне обидва шари, і в грі зʼявляються вертикальний
     *  та горизонтальний скрол. Блокуємо документ на час гри і повертаємо як було. */
    useEffect(() => {
        const { body } = document;
        const html = document.documentElement;
        const prev = {
            bodyOverflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            overscroll: body.style.overscrollBehavior,
        };
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        body.style.overscrollBehavior = 'none';
        return () => {
            body.style.overflow = prev.bodyOverflow;
            html.style.overflow = prev.htmlOverflow;
            body.style.overscrollBehavior = prev.overscroll;
        };
    }, []);

    const handleEvents = useCallback(
        (events: GameEvent[], fx: FxState, layout: FieldLayout): void => {
            const sound = soundRef.current;
            for (const event of events) {
                if (event.type === 'catch') {
                    if (event.kind === 'seed' || event.kind === 'gold') {
                        streakRef.current += 1;
                        if (event.kind === 'gold') sound?.ding();
                        else sound?.pop(streakRef.current);
                    }
                    fxCatchBurst(fx, event.x * FIELD.refW, layout.catchY - 20, event.kind, event.score);
                } else if (event.type === 'hit') {
                    streakRef.current = 0;
                    setLivesLeft(event.livesLeft);
                    sound?.thud();
                    fxHitBurst(fx, event.x * FIELD.refW, layout.catchY - 10);
                    if (navigator.vibrate) navigator.vibrate(60);
                } else if (event.type === 'threshold') {
                    sound?.threshold();
                    if (navigator.vibrate) navigator.vibrate(40);
                    onThreshold();
                } else if (event.type === 'multiplierStart') {
                    setMultActive(true);
                } else if (event.type === 'multiplierEnd') {
                    setMultActive(false);
                }
            }
        },
        [onThreshold],
    );

    const finishGame = useCallback(
        (engine: GameEngine): void => {
            const { state } = engine;
            const won = state.thresholdTakenAt !== null;
            const isNewRecord = onFinish(state.score);
            soundRef.current?.finish(won);
            const summary: GameEndSummary = {
                score: state.score,
                won,
                burned: state.finishReason === 'lives',
                seconds: Math.round(state.time),
                isNewRecord,
            };
            setEnd(summary);
            onGameEnd(won);
            setPhase('end');
        },
        [onFinish, onGameEnd],
    );

    /** Розмір канви й фон — ОКРЕМО від циклу гри: перерахунок розкладки не
     *  повинен створювати новий рушій, інакше ресайз чи поворот скидали б
     *  партію на нуль. Фон кешований, тож повторний виклик майже безкоштовний. */
    useEffect(() => {
        backgroundRef.current = renderBackground(layout);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const boxW = layout.stageW + 2 * layout.offsetX;
        const boxH = layout.worldH * layout.cssScale;
        canvas.width = Math.round(boxW * layout.dpr);
        canvas.height = Math.round(boxH * layout.dpr);
    }, [layout, phase]);

    useEffect(() => {
        if (phase !== 'play') return undefined;
        const canvas = canvasRef.current;
        if (!canvas || !spritesReady) return undefined;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;

        const engine = createGameEngine(Date.now() % 2147483647);
        engineRef.current = engine;
        const fx = createFx();
        let raf = 0;
        let lastTs: number | null = null;

        const frame = (ts: number): void => {
            const layout = layoutRef.current;
            const background = backgroundRef.current;
            const sprites = spritesRef.current;
            // Пауза (ландшафт) або ще не готовий кадр: світ стоїть, rAF живий.
            if (pausedRef.current || !background || !sprites) {
                lastTs = null;
                raf = window.requestAnimationFrame(frame);
                return;
            }

            // Tab hidden = pause (visibilitychange мітигація): the session
            // survives a phone call; only closing the page burns the try.
            if (lastTs === null) lastTs = ts;
            const dt = Math.min((ts - lastTs) / 1000, 1 / 30);
            lastTs = ts;

            const events = engine.tick(dt, targetXRef.current);
            handleEvents(events, fx, layout);
            drawFrame(ctx, background, sprites, engine.state, fx, dt, layout);

            if (barFillRef.current) {
                const pct = Math.min((engine.state.score / C.THRESHOLD) * 100, 100);
                barFillRef.current.style.width = `${pct}%`;
            }
            if (timerRef.current) {
                const left = Math.max(Math.ceil(C.DURATION - engine.state.time), 0);
                timerRef.current.textContent = String(left);
                timerRef.current.parentElement?.classList.toggle(
                    styles.timerGold,
                    left <= 10,
                );
            }
            const multLeft = engine.state.multUntil - engine.state.time;
            if (multPieRef.current && multLeft > 0) {
                multPieRef.current.style.setProperty(
                    '--pie',
                    `${(multLeft / C.MULTIPLIER.duration) * 100}%`,
                );
            }
            setMultEnding(multLeft > 0 && multLeft < 1);

            if (engine.state.finished) {
                finishGame(engine);
                return;
            }
            raf = window.requestAnimationFrame(frame);
        };
        raf = window.requestAnimationFrame(frame);

        const onVisibility = (): void => {
            lastTs = null;
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.cancelAnimationFrame(raf);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [phase, spritesReady, layoutRef, handleEvents, finishGame]);

    const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>): void => {
        const rect = event.currentTarget.getBoundingClientRect();
        // Сцена вужча за коробку, коли є поля з боків — ділити треба на неї.
        const { offsetX, stageW } = layoutRef.current;
        const x = (event.clientX - rect.left - offsetX) / stageW;
        targetXRef.current = Math.min(Math.max(x, 0), 1);
    }, [layoutRef]);

    const startGame = useCallback((): void => {
        onTryStart();
        setPhase('play');
    }, [onTryStart]);

    const hearts = (
        <div className={styles.hearts}>
            {[0, 1, 2].map((i) => (
                <HeartIcon key={i} lost={i >= livesLeft} />
            ))}
        </div>
    );

    const stageVars = {
        ...GAME_CSS_VARS,
        '--stage-x': `${layout.offsetX}px`,
        '--stage-w': `${layout.stageW}px`,
    } as React.CSSProperties;

    return (
        <div ref={overlayRef} className={styles.overlay} style={stageVars}>
            {phase === 'start' && (
                <div className={styles.startScreen}>
                    <BackgroundCanvas layout={layout} />
                    <div className={styles.startHead}>
                        <h2 className={styles.title}>{t('Game.Title')}</h2>
                        {record > 0 && (
                            <div className={styles.recordPill}>★ {t('Game.Record', { score: record })}</div>
                        )}
                    </div>
                    <div className={styles.startMiddle}>
                    <div className={styles.rules}>
                        <div className={styles.decoStar}>
                            <SpritePreview sprites={sprites} kind="golden" cssSize={52} />
                        </div>
                        <div className={styles.rulePlate}>
                            <span className={styles.ruleIcon}>
                                <SpritePreview sprites={sprites} kind="item" />
                            </span>
                            <span>+{C.SCORE.seed}</span>
                        </div>
                        <div className={styles.rulePlate}>
                            <span className={`${styles.ruleIcon} ${styles.ruleIconHalo}`}>
                                <SpritePreview sprites={sprites} kind="golden" />
                            </span>
                            <span>+{C.SCORE.gold}</span>
                        </div>
                        <div className={styles.rulePlate}>
                            <span className={styles.ruleIcon}>
                                <SpritePreview sprites={sprites} kind="bugLight" />
                            </span>
                            <HeartIcon size={20} broken />
                        </div>
                    </div>
                    <p className={styles.goal}>{t('Game.Goal', { threshold: C.THRESHOLD })}</p>
                    </div>
                    <div className={styles.startFoot}>
                    <div className={styles.startControls}>
                        <button
                            type="button"
                            className={styles.soundButton}
                            aria-label={t('Game.Sound') ?? 'Sound'}
                            onClick={() => onSoundToggle(!soundEnabled)}
                        >
                            <NoteIcon size={26} muted={!soundEnabled} />
                        </button>
                        <button type="button" className={styles.playButton} onClick={startGame} aria-label={t('Game.Play') ?? 'Play'}>
                            <span className={styles.playTriangle} />
                        </button>
                    </div>
                    <CatcherPreview sprites={sprites} />
                    <button type="button" className={styles.closeLink} onClick={() => onClose('dismissed')}>
                        {t('Game.NotNow')}
                    </button>
                    </div>
                </div>
            )}

            {phase === 'play' && (
                <div
                    className={`${styles.playField} ${multActive ? styles.multVignette : ''} ${multEnding ? styles.multEnding : ''}`}
                    onPointerMove={onPointerMove}
                    onPointerDown={onPointerMove}
                >
                    <canvas ref={canvasRef} className={styles.canvas} />
                    <div ref={setHudEl} className={styles.hud}>
                        <div className={styles.barRow}>
                            <div className={styles.bar}>
                                <div ref={barFillRef} className={styles.barFill} />
                            </div>
                            <div className={styles.giftSlot}>
                                <GiftBoxIcon size={30} />
                            </div>
                        </div>
                        <div className={styles.statusRow}>
                            {hearts}
                            {multActive && (
                                <span className={styles.multBadge}>
                                    <span ref={multPieRef} className={styles.multPie} />
                                    ×2
                                </span>
                            )}
                            <div className={styles.timerPill}>
                                <span ref={timerRef}>{C.DURATION}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!layout.playable && (
                <div className={styles.rotateCard} role="status">
                    <span className={styles.rotateIcon} aria-hidden />
                    <p className={styles.rotateTitle}>{t('Game.Rotate.Title')}</p>
                    <p className={styles.rotateHint}>{t('Game.Rotate.Hint')}</p>
                </div>
            )}

            {phase === 'end' && end && (
                <div className={`${styles.endScreen} ${styles.irisIn}`}>
                    <BackgroundCanvas layout={layout} />
                    {end.won && !end.burned && (
                        <div className={styles.confetti} aria-hidden>
                            {Array.from({ length: 18 }).map((_, i) => (
                                <i
                                    key={i}
                                    style={{
                                        left: `${(i * 53) % 100}%`,
                                        animationDelay: `${(i % 6) * 0.4}s`,
                                        background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                                    }}
                                />
                            ))}
                        </div>
                    )}
                    <div className={styles.endCard}>
                        {end.burned && (
                            <>
                                <div className={styles.endBug}><SpritePreview sprites={sprites} kind="bug" /></div>
                                <span className={styles.crumbs} aria-hidden />
                            </>
                        )}
                        <div className={`${styles.endGift} ${end.won ? styles.endGiftNimb : ''}`}>
                            <GiftBoxIcon size={64} opened={end.won} ajar={!end.won && !end.burned} />
                        </div>
                        {end.burned && (
                            <div className={styles.endHearts}>
                                {[0, 1, 2].map((i) => (
                                    <HeartIcon key={i} size={28} lost onLight />
                                ))}
                            </div>
                        )}
                        {!end.won && (
                            <>
                                <div className={styles.missBar}>
                                    <div
                                        className={styles.missFill}
                                        style={{ width: `${Math.min((end.score / C.THRESHOLD) * 100, 100)}%` }}
                                    />
                                </div>
                                <p className={styles.endFraction}>
                                    {end.score} / {C.THRESHOLD}
                                </p>
                            </>
                        )}
                        <h3 className={styles.endTitle}>
                            {end.won && !end.burned && t('Game.Win.Title')}
                            {end.burned && t('Game.Burned.Title')}
                            {!end.won && !end.burned && t('Game.NearMiss.Title')}
                        </h3>
                        {end.won && <p className={styles.endScore}>{end.score}</p>}
                        {end.won && (
                            <div className={styles.successPill}>
                                {t('Game.Win.PointAdded')} ✓
                            </div>
                        )}
                        {end.burned && (
                            <p className={styles.endNote}>{t('Game.Burned.HeldOut', { seconds: end.seconds })}</p>
                        )}
                        {end.isNewRecord && <p className={styles.endNote}>{t('Game.NewRecord')}</p>}
                        {!end.won && <p className={styles.endNext}>{t('Game.NextTry')}</p>}
                        <button type="button" className={styles.doneButton} onClick={() => onClose('done')}>
                            {t('Game.Done')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export { GameScreen };
